<template>
  <div id="apt-market-summary">
    <div class="kpi-grid">
      <div class="kpi-card">
        <div class="kpi-label">최근 12개월 거래량</div>
        <template v-if="loading">
          <div class="kpi-sk kpi-sk-val"></div>
          <div class="kpi-sk kpi-sk-delta"></div>
        </template>
        <template v-else>
          <div class="kpi-value">{{ recentCount.toLocaleString() }}<span class="unit">건</span></div>
          <div class="kpi-delta" :class="deltaClass(countYoy)">전년 동기 대비 {{ deltaText(countYoy) }}</div>
        </template>
      </div>

      <div class="kpi-card">
        <div class="kpi-label">최근 12개월 평균가</div>
        <template v-if="loading">
          <div class="kpi-sk kpi-sk-val"></div>
          <div class="kpi-sk kpi-sk-delta"></div>
        </template>
        <template v-else>
          <div class="kpi-value">{{ recentAvg.toFixed(1) }}<span class="unit">억</span></div>
          <div class="kpi-delta" :class="deltaClass(avgYoy)">전년 동기 대비 {{ deltaText(avgYoy) }}</div>
        </template>
      </div>

      <div class="kpi-card">
        <div class="kpi-label">최근 1년 최고 평균가</div>
        <template v-if="loading">
          <div class="kpi-sk kpi-sk-val"></div>
          <div class="kpi-sk kpi-sk-delta"></div>
        </template>
        <template v-else>
          <div class="kpi-value">{{ peakAmount.toFixed(1) }}<span class="unit">억</span></div>
          <div class="kpi-delta neutral" v-if="peakLabel">{{ peakLabel }} 기준</div>
        </template>
      </div>
    </div>
    <p class="kpi-note">
      국토교통부 신고 특성상 최근 1~2개월 거래는 확정 전이라 수치가 늘어날 수 있습니다.
      <span v-if="dataAsOf">· 데이터 기준 {{ dataAsOf }}</span>
    </p>
    <router-link to="/apt/stats" class="kpi-analyze" v-if="!loading && avgYoy !== null">
      평균가 {{ avgYoy > 0 ? '상승' : '하락' }}({{ deltaText(avgYoy) }})이 실제 시세 변동인지, 거래 구성(평형) 변화인지 분석하기 →
    </router-link>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";
import { SeoulTradeStat, SeoulTradeStatRequest } from "../../class";

export default defineComponent({
  name: "AptMarketSummary",
  mixins: [UtilMixin],
  data() {
    return {
      loading: true,
      recentCount: 0,
      recentAvg: 0,
      countYoy: null as number | null,
      avgYoy: null as number | null,
      peakAmount: 0,
      peakLabel: "",
      dataAsOf: "",
    };
  },
  methods: {
    deltaClass(v: number | null) {
      if (v === null) return "neutral";
      if (v > 0.05) return "up";
      if (v < -0.05) return "down";
      return "flat";
    },
    deltaText(v: number | null) {
      if (v === null) return "—";
      const sign = v > 0 ? "▲" : v < 0 ? "▼" : "―";
      return `${sign} ${Math.abs(v).toFixed(1)}%`;
    },
    async fetchData() {
      this.loading = true;
      try {
        const now = new Date();
        // 최근 24개월(전년 동기 비교용)
        const start = new Date(now.getFullYear(), now.getMonth() - 23, 1);
        const fmt = (d: Date) =>
          `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}01`;
        const req = new SeoulTradeStatRequest(fmt(start), fmt(now), "MONTH");
        const res = await this.getApi().apt().getSeoulTradeStats(req);
        if (!Array.isArray(res) || res.length === 0) return;

        const rows = (res as SeoulTradeStat[])
          .slice()
          .sort((a, b) => a.year * 100 + a.month - (b.year * 100 + b.month));

        const last12 = rows.slice(-12);
        const prev12 = rows.slice(-24, -12);

        const sum = (arr: SeoulTradeStat[]) =>
          arr.reduce((s, r) => s + (r.totalCount || 0), 0);
        const weightedAvg = (arr: SeoulTradeStat[]) => {
          let amt = 0;
          let cnt = 0;
          arr.forEach((r) => {
            if (r.totalAvgAmount && r.totalCount) {
              amt += r.totalAvgAmount * r.totalCount;
              cnt += r.totalCount;
            }
          });
          return cnt ? amt / cnt : 0;
        };

        this.recentCount = sum(last12);
        this.recentAvg = weightedAvg(last12); // totalAvgAmount 는 이미 억 단위
        const prevCount = sum(prev12);
        const prevAvg = weightedAvg(prev12);

        this.countYoy =
          prevCount > 0 ? ((this.recentCount - prevCount) / prevCount) * 100 : null;
        this.avgYoy =
          prevAvg > 0 ? ((this.recentAvg - prevAvg) / prevAvg) * 100 : null;

        // 최근 12개월 중 최고 평균가 월
        let peak = last12[0];
        last12.forEach((r) => {
          if ((r.totalAvgAmount || 0) > (peak.totalAvgAmount || 0)) peak = r;
        });
        if (peak) {
          this.peakAmount = peak.totalAvgAmount || 0;
          this.peakLabel = `${peak.year}.${String(peak.month).padStart(2, "0")}`;
        }

        const lastRow = rows[rows.length - 1];
        if (lastRow)
          this.dataAsOf = `${lastRow.year}.${String(lastRow.month).padStart(2, "0")}`;
      } catch {
        // ignore
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchData();
  },
});
</script>
